import { Component, computed, effect, inject, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { UserPageStore } from './user-page.store';
import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import { BreadcrumbService } from 'xng-breadcrumb';
import { BreadcrumbsEnum } from '../../../../shared/models/breadcrumbs.enum';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UserUpdateInput } from '@libs/types/src/user/input-type/user/user-update.input';
import { isInvalid } from '../../../../shared/helpers/validate';
import { contentGlobalSuccess } from '../../../content.error';
import { ActivatedRoute, Router } from '@angular/router';
import { onEventMarker } from '../../../../../store/global/event';
import { EntityEventMessengerEnum } from '../../../../../../../../types/src/common/enum/entity-event-messenger.enum';
import { FileObject, ServiceGridItemObject, UserGridItemObject } from '@it2go/types';
import { FilterBuilder } from '../../../helper/filter.builder';
import { goBack, isAllowed, routeParams } from '../../../../shared/shared.global-service';
import {
    UserActivateDialogComponent,
} from '../../../components/user-management/user-activete-dialog/user-activate-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { UserRoleComponent } from '../../../../../components/user-management/user-detail/user-role/user-role.component';
import { UserPageStoreV2 } from './user-page.store-v2';
import { toSignal } from '@angular/core/rxjs-interop';
import { GlobalStore } from '../../../../../store/global.global-store';
import { userUri } from '../../../../../utils/router.utils';
import { BaseDialogService } from '../../../../../components/kit/base-dialog/base-dialog-service';
import { formatPerson } from '@libs/shared/helpers/person.utils';
import { UserBasicInfoComponent } from '../../../components/user-management/user-basic-info/user-basic-info.component';
import {
    UserContactInfoCardComponent,
} from '../../../components/user-management/user-contact-info-card/user-contact-info-card.component';
import {
    UserPersonalInfoComponent,
} from '../../../components/user-management/user-personal-info/user-personal-info.component';
import {
    UserExperienceTableComponent,
} from '../../../components/user-management/user-experience-table/user-experience-table.component';
import {
    UserEmploymentsComponent,
} from '../../../components/user-management/user-employments/user-employments.component';
import {
    UserEmploymentInfoComponent,
} from '../../../components/user-management/user-employment-info/user-employment-info.component';
import {
    UserWorkplaceTableComponent,
} from '../../../components/user-management/user-workplace-table/user-workplace-table.component';
import { ActionButtonsComponent } from '../../../../../components/kit/action-buttons/action-buttons.component';
import { DeleteButtonComponent } from '../../../../../components/kit/button/delete-button.component';

@Component({
    selector: 'app-user-page',
    templateUrl: 'user-page.component.html',
    styleUrl: 'user-page.component.scss',
    providers: [UserPageStore, UserPageStoreV2],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ActionButtonsComponent,
        DeleteButtonComponent,
        UserBasicInfoComponent,
        UserContactInfoCardComponent,
        UserEmploymentsComponent,
        UserEmploymentInfoComponent,
        UserExperienceTableComponent,
        UserPersonalInfoComponent,
        UserRoleComponent,
        UserWorkplaceTableComponent,
    ],
})
export class UserPageComponent extends SubscriptionComponent implements OnInit {

    public readonly storeV2 = inject(UserPageStoreV2);
    public readonly globalStore = inject(GlobalStore);
    protected readonly store = inject(UserPageStore);
    private readonly breadcrumbs = inject(BreadcrumbService);
    private readonly fb = inject(FormBuilder);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly dialogService = inject(BaseDialogService);
    private readonly translate = inject(TranslateService);


    @ViewChild('userRules') userRoles!: UserRoleComponent;

    protected form = this.fb.group({
        active: this.fb.control(true),
        organizationId: this.fb.control(0),
        name: this.fb.control(''),
        surname: this.fb.control(''),
        email: this.fb.control('', Validators.email),
        titleBeforeName: this.fb.control(''),
        titleAfterName: this.fb.control(''),
        sex: this.fb.control<string | null>('', [Validators.required]),
        phone: this.fb.control(''),
        fax: this.fb.control(''),
        personalEmail: this.fb.control('', Validators.email),
        mobile: this.fb.control(''),
        employeeCode: this.fb.control(''),
        prevYearsOfExperience: this.fb.control<number | null>(null),
        educationCode: this.fb.control(''),
        fieldOfStudy: this.fb.control(''),
        icpOfDoctor: this.fb.control(''),
        doctorSpecialization: this.fb.control(''),
        groups: this.fb.control<number[]>([]),
        rules: this.fb.control<string[]>([]),
        birthDate: this.fb.control<Date | null>(null),
    });

    protected passwordForm = this.fb.group({
        password: this.fb.control(''),
    });

    protected serviceForms: Record<number, FormGroup> = {};

    protected user: UserGridItemObject | null = null;
    protected services: ServiceGridItemObject[] = [];

    protected userChange = toSignal(this.store.user$);
    protected canEditUser = computed(() =>
        isAllowed['user.update']() && this.userChange()?.canEdit === true,
    );

    constructor() {
        super();
        this.store.onRouteChange('getUsers', { id: 'id' }, { asFilter: true });
        effect(() => {
            const organizationId = this.storeV2.globalGlobalStore.organizationStore.selectedId();
            const user = this.userChange();
            if (user && organizationId) {
                const matched = user.organizations?.find((it) => it.id === organizationId);
                if (!matched) {
                    this.router.navigate(['../'], { relativeTo: this.route }).then();
                }
            }
        });
        this.store.doOnActions(
            () => this.store.api.getUsers(FilterBuilder.singleItem({ id: this.route.snapshot.params['id'] || 0 })),
            'updateUser', 'updateUserPhoto',
        );
        this.store.doOnActions(
            () => this.goToUsers(),
            'removeAccess',
        );
        this.store.doOnActions(
            () => this.store.api.getUserWorkplaces(FilterBuilder.singlePage({ userId: this.user?.id })),
            'createUserWorkplace', 'updateUserWorkplace', 'deleteUserWorkplace',
        );

        this.store.registerSuccessMessage('updateUser', 'user.messages.updated');

        this.subs.push(
            this.store.user$.subscribe(async (it) => {
                if (!it) {
                    this.form.reset();
                    return;
                }
                this.user = it;
                this.breadcrumbs.set(`@${BreadcrumbsEnum.User}`, `${BreadcrumbsEnum.User}|${formatPerson(it)}`);
                this.form.patchValue({
                    ...it as any,
                    birthDate: it.birthDate ? new Date(it.birthDate) : null,
                });
                if (it.canEdit && isAllowed['user.update']()) {
                    this.form.enable({ emitEvent: false });
                    this.passwordForm.enable({ emitEvent: false });
                } else {
                    this.form.disable({ emitEvent: false });
                    this.passwordForm.disable({ emitEvent: false });
                }
            }),
            this.store.services$.subscribe((it) => {
                this.services = it;
                this.serviceForms = {};
                this.services.forEach((it) => {
                    this.serviceForms[it.id] = this.fb.group({
                        serviceId: this.fb.control<number>(it.id),
                        groups: this.fb.control<number[]>([]),
                        rules: this.fb.control<string[]>([]),
                    });
                });
            }),
            this.form.controls.active.valueChanges.subscribe((active) => {
                if (this.form.controls.active.dirty && active) {
                    this.dialogService.open(UserActivateDialogComponent, {
                        data: { userId: this.user?.id },
                        header: this.translate.instant('user.activateUser.title'),
                    });
                }
            }),
        );
    }

    ngOnInit() {
        if (!this.canEditUser()) {
            this.form.disable();
            this.passwordForm.disable();
        }
    }

    protected async save(): Promise<void> {
        if (isInvalid(this.form)) return;
        const { rules, groups, globalGroups, globalRules } = this.userRoles.getRoles();

        if (this.passwordForm.value.password) {
            this.setUserPassword();
        }

        const data = {
            id: routeParams.id(),
            active: this.form.value.active,
            name: this.form.value.name,
            surname: this.form.value.surname,
            email: this.form.value.email,
            titleBeforeName: this.form.value.titleBeforeName,
            titleAfterName: this.form.value.titleAfterName,
            phone: this.form.value.phone,
            mobile: this.form.value.mobile,
            sex: this.form.value.sex,
            fax: this.form.value.fax,
            personalEmail: this.form.value.personalEmail,
            employeeCode: this.form.value.employeeCode,
            prevYearsOfExperience: this.form.value.prevYearsOfExperience,
            birthDate: this.form.value.birthDate?.toISOString(),
            educationCode: this.form.value.educationCode,
            fieldOfStudy: this.form.value.fieldOfStudy,
            icpOfDoctor: this.form.value.icpOfDoctor,
            doctorSpecialization: this.form.value.doctorSpecialization,
            rules,
            groups,
            globalRules,
            globalGroups,
            organizationId: this.store.organizationId,
        } as UserUpdateInput;

        this.store.api.updateUser(data);

        if (routeParams.id() == this.globalStore.loggedUserStore.currentUserId()) {
            this.globalStore.refreshAcl().then();
        }

        contentGlobalSuccess.next('common.updated');
        goBack();
    }

    protected async delete(): Promise<void> {
        this.store.api.removeAccess({
            userId: routeParams.id(),
            organizationId: this.store.organizationId,
        });
        await onEventMarker(EntityEventMessengerEnum.ORGANIZATION_USER_DELETE);
        contentGlobalSuccess.next('user.messages.deleted');
        await this.router.navigate(['../'], { relativeTo: this.route });
    }

    protected setUserPassword(): void {
        if (!this.passwordForm.value.password) return;

        // TODO Tohle se odignoruje (možná kvůli novému modalu?) - prozatím se prostě pošle, bylo-li nastaveno
        // this.confirmation.confirm(
        //     'user.messages.setPassword',
        //     'user.messages.setPasswordDesc',
        //     () => {
        this.storeV2.api.setUserPassword({
            userId: routeParams.id(),
            password: this.passwordForm.value.password || '',
        }).then(() => {
            contentGlobalSuccess.next('user.messages.setPasswordSuccess');
        });
        // },
        // {},
        // 'pi-key',
        // );
    }

    public onAvatarUpload(file: FileObject): void {
        this.storeV2.api.updateUserPhoto({
            id: routeParams.id(),
            image: file.name,
        }).then(() => {
            contentGlobalSuccess.next('user.messages.photoUpdated');
            if (routeParams.id() == this.globalStore.loggedUserStore.currentUserId()) {
                this.storeV2.api.whoami().then();
            }
        });
    }

    protected async goToGroups(): Promise<void> {
        await this.router.navigateByUrl(`/content/organizations/${this.store.organizationId}/acl`);
    }

    protected async goToUsers(): Promise<void> {
        await this.router.navigateByUrl(userUri());
    }

    protected readonly isAllowed = isAllowed;
}
